import { motion } from 'framer-motion';
import { Calendar, MapPin, RefreshCw, Users, Clock } from 'lucide-react';
import { useAdminBookingsSummary } from '../api/useAdminBookingsSummary';
import { Loader } from '@/components/Loader';

// Color palette for cities
const CITY_COLORS = [
  '#8B5CF6', // primary purple
  '#06B6D4', // cyan
  '#F59E0B', // amber
  '#10B981', // green
  '#EF4444', // red
  '#EC4899', // pink
  '#6366F1', // indigo
  '#84CC16', // lime
  '#F97316', // orange
  '#14B8A6', // teal
];

export default function AdminBookings() {
  const { data: summaryData, isLoading, refetch } = useAdminBookingsSummary();

  const data = summaryData?.data;
  const events = data?.events || [];
  const cities = data?.cities || [];

  // Create a map for consistent city colors
  const cityColorMap = new Map<string, string>();
  cities.forEach((city, index) => {
    cityColorMap.set(city.id, CITY_COLORS[index % CITY_COLORS.length]);
  });

  const formatDateShort = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    // Assuming time is in HH:MM:SS format
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Calculate totals per city across all events
  const cityTotals = new Map<string, number>();
  events.forEach(event => {
    event.bookingsByCity.forEach(cityData => {
      cityTotals.set(cityData.cityId, (cityTotals.get(cityData.cityId) || 0) + cityData.count);
    });
  });

  const totalBookings = events.reduce((sum, e) => sum + e.totalBookings, 0);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">Bookings Overview</h1>
            <p className="text-dark-muted text-lg">
              Bookings per city for recent and upcoming events
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="btn btn-ghost self-start"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </motion.div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      ) : events.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-12"
        >
          <Calendar className="w-16 h-16 mx-auto text-dark-muted mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Events Found</h2>
          <p className="text-dark-muted">There are no events to display bookings for.</p>
        </motion.div>
      ) : (
        <>
          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{events.length}</p>
                  <p className="text-dark-muted text-xs">Events Shown</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalBookings}</p>
                  <p className="text-dark-muted text-xs">Total Bookings</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{events.filter(e => !e.isPast).length}</p>
                  <p className="text-dark-muted text-xs">Upcoming Events</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-cyan-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{cityTotals.size}</p>
                  <p className="text-dark-muted text-xs">Active Cities</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Events Table - Cities as rows, Dates as columns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card mb-8"
          >
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-500" />
              Bookings by City
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left py-3 px-4 text-dark-muted text-sm font-medium">City</th>
                    {/* Dynamic event date columns */}
                    {events.map(event => (
                      <th 
                        key={event.eventTimeId} 
                        className={`text-center py-3 px-4 text-sm font-medium whitespace-nowrap ${
                          event.isPast ? 'opacity-60' : ''
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span>{formatDateShort(event.date)}</span>
                          <span className="text-xs text-dark-muted">{formatTime(event.time)}</span>
                          {event.isPast ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-gray-500/20 text-gray-400">
                              Past
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-green-500/20 text-green-400">
                              Upcoming
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="text-right py-3 px-4 text-dark-muted text-sm font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {/* City rows - sorted by total bookings */}
                  {Array.from(cityTotals.entries())
                    .sort((a, b) => b[1] - a[1])
                    .map(([cityId, total]) => {
                      const city = cities.find(c => c.id === cityId);
                      const cityName = city?.name || cityId;
                      const color = cityColorMap.get(cityId) || CITY_COLORS[0];

                      return (
                        <tr 
                          key={cityId} 
                          className="border-b border-dark-border/50 hover:bg-dark-border/20 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: color }}
                              />
                              <span className="font-medium" style={{ color }}>
                                {cityName}
                              </span>
                            </div>
                          </td>
                          {/* Dynamic event values */}
                          {events.map(event => {
                            const cityData = event.bookingsByCity.find(c => c.cityId === cityId);
                            const count = cityData?.count || 0;
                            return (
                              <td 
                                key={event.eventTimeId} 
                                className={`text-center py-3 px-4 ${event.isPast ? 'opacity-60' : ''}`}
                              >
                                <span className={count > 0 ? 'font-semibold' : 'text-dark-muted'}>
                                  {count > 0 ? count : '-'}
                                </span>
                              </td>
                            );
                          })}
                          <td className="text-right py-3 px-4">
                            <span className="font-bold" style={{ color }}>
                              {total}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  {/* Totals row */}
                  <tr className="bg-dark-border/30 font-semibold">
                    <td className="py-3 px-4">Total</td>
                    {events.map(event => (
                      <td 
                        key={event.eventTimeId} 
                        className={`text-center py-3 px-4 ${event.isPast ? 'opacity-60' : ''}`}
                      >
                        <span className="font-bold">{event.totalBookings}</span>
                      </td>
                    ))}
                    <td className="text-right py-3 px-4 text-lg">{totalBookings}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* City Breakdown Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-500" />
              Bookings by City
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Array.from(cityTotals.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([cityId, total]) => {
                  const city = cities.find(c => c.id === cityId);
                  const cityName = city?.name || cityId;
                  const color = cityColorMap.get(cityId) || CITY_COLORS[0];
                  const percentage = totalBookings > 0 
                    ? ((total / totalBookings) * 100).toFixed(1) 
                    : '0';

                  return (
                    <div 
                      key={cityId}
                      className="card p-4"
                      style={{ borderLeft: `4px solid ${color}` }}
                    >
                      <div className="text-sm text-dark-muted mb-1">{cityName}</div>
                      <div className="text-2xl font-bold" style={{ color }}>
                        {total}
                      </div>
                      <div className="text-xs text-dark-muted mt-1">
                        {percentage}% of total
                      </div>
                    </div>
                  );
                })}
            </div>
          </motion.div>

          {/* Legend / Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center text-dark-muted text-sm"
          >
            Showing the most recent past event and up to 4 upcoming events
          </motion.div>
        </>
      )}
    </div>
  );
}
